import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import useClosedPeriod from '../../../hooks/useClosedPeriod';

/**
 * Bulk entry — many penalty / rebate debit notes in one screen, one note per row (any supplier).
 * Rows are checked together on save: if one has a problem nothing is saved and the row is marked.
 * Price / quantity adjustments come from GRN lines — use "From GRNs" for those.
 */
const TYPES = [{ key: 'PENALTY', label: 'Penalty' }, { key: 'REBATE', label: 'Rebate' }];
const fmt = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DebitNoteBulkEntry = ({ onClose, onSuccess }) => {
  const [defaults, setDefaults] = useState({});
  const [debitDate, setDebitDate] = useState(new Date().toISOString().split('T')[0]);
  const [costCenter, setCostCenter] = useState('');
  const [costCenterLabel, setCostCenterLabel] = useState('');
  const [rows, setRows] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const blankRow = (type = 'PENALTY', d = defaults) => ({
    key: Math.random().toString(36).slice(2),
    supplier: '', supplierLabel: '', noteType: type, description: '', reference: '', amount: '', taxAmount: '',
    ledgerAccountId: d[type]?.accountId || '', ledgerAccountLabel: d[type]?.label || ''
  });

  const { minDate, dateError } = useClosedPeriod();

  const closedError = dateError(debitDate);


  useEffect(() => {
    document.body.style.overflow = 'hidden';
    inventoryApi.getDebitNoteChargeDefaults()
      .then(res => { const d = res.data || {}; setDefaults(d); setRows([blankRow('PENALTY', d), blankRow('PENALTY', d), blankRow('PENALTY', d)]); })
      .catch(() => setRows([blankRow(), blankRow(), blankRow()]));
    return () => { document.body.style.overflow = 'unset'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (key, patch) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
    setRowErrors(prev => { const next = { ...prev }; delete next[key]; return next; });
  };
  const changeType = (r, type) => {
    const wasDefault = !r.ledgerAccountId || r.ledgerAccountId === defaults[r.noteType]?.accountId;
    update(r.key, {
      noteType: type,
      ...(type === 'PENALTY' && { taxAmount: '' }),
      ...(wasDefault && { ledgerAccountId: defaults[type]?.accountId || '', ledgerAccountLabel: defaults[type]?.label || '' })
    });
  };
  const duplicate = (r) => setRows(prev => {
    const i = prev.findIndex(x => x.key === r.key);
    const copy = { ...r, key: Math.random().toString(36).slice(2), supplier: '', supplierLabel: '' };
    return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
  });

  // rows the user started; fully blank rows are ignored
  const filled = rows.filter(r => r.supplier || r.description || (parseFloat(r.amount) || 0) > 0);
  const total = filled.reduce((t, r) => t + (parseFloat(r.amount) || 0) + (r.noteType === 'REBATE' ? (parseFloat(r.taxAmount) || 0) : 0), 0);

  const localError = (r) => {
    if (!r.supplier) return 'Select the supplier';
    if (!String(r.description).trim()) return 'Enter a description';
    if (!((parseFloat(r.amount) || 0) > 0)) return 'Enter the amount';
    if (!r.ledgerAccountId) return 'Choose the account to credit';
    if ((parseFloat(r.taxAmount) || 0) < 0) return 'Tax cannot be negative';
    return null;
  };

  const handleSave = async () => {
    if (!filled.length) { toast.error('Fill in at least one row'); return; }
    const errs = {};
    filled.forEach(r => { const e = localError(r); if (e) errs[r.key] = e; });
    if (Object.keys(errs).length) { setRowErrors(errs); toast.error(`${Object.keys(errs).length} row(s) need fixing`); return; }

    setSaving(true);
    try {
      const res = await inventoryApi.bulkCreateDebitNotes({
        debitDate,
        costCenter: costCenter || null,
        costCenterLabel,
        notes: filled.map(r => ({
          supplier: r.supplier,
          noteType: r.noteType,
          description: r.description.trim(),
          reference: r.reference.trim(),
          amount: parseFloat(r.amount) || 0,
          taxAmount: r.noteType === 'REBATE' ? (parseFloat(r.taxAmount) || 0) : 0,
          ledgerAccount: { accountId: r.ledgerAccountId, label: r.ledgerAccountLabel }
        }))
      });
      toast.success(res.message || 'Debit notes created');
      onSuccess?.(res.data);
    } catch (err) {
      const data = err.response?.data;
      if (Array.isArray(data?.errors)) {
        const errsByKey = {};
        data.errors.forEach(e => { const r = filled[e.row - 1]; if (r) errsByKey[r.key] = e.message; });
        setRowErrors(errsByKey);
      }
      toast.error(data?.message || 'Failed to create the debit notes');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500';

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-[96rem] max-h-[94vh] bg-white shadow-2xl rounded-2xl flex flex-col overflow-hidden">
        <div className="flex-shrink-0 bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Bulk Debit Notes — Entry</h3>
            <p className="text-xs text-amber-100">Penalties and rebates for many suppliers at once · one debit note per row · all pending approval</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">✕</button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Debit Date (all rows)</label>
              <input type="date" value={debitDate} min={minDate} onChange={(e) => setDebitDate(e.target.value)} className={input} />
              {closedError && <p className="text-xs text-red-600 mt-0.5">{closedError}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Cost Center (optional, all rows)</label>
              <SearchableCostCenterDropdown value={costCenter} displayLabel={costCenterLabel}
                onChange={(id, label) => { setCostCenter(id || ''); setCostCenterLabel(label || ''); }} placeholder="Optional" menuPlacement="down" />
            </div>
            <div className="md:col-span-2 text-xs text-gray-500 self-end">
              Default accounts — Penalty: <span className="font-medium text-gray-700">{defaults.PENALTY?.label || 'not set'}</span> ·
              Rebate: <span className="font-medium text-gray-700">{defaults.REBATE?.label || 'not set'}</span>
              {(!defaults.PENALTY || !defaults.REBATE) && ' (set them in Finance → Transaction Ledger Mapping, or choose per row)'}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]" data-testid="bulk-entry-table">
              <thead className="bg-gray-100 text-xs text-gray-600">
                <tr>
                  <th className="px-2 py-2 text-left w-8">#</th>
                  <th className="px-2 py-2 text-left w-64">Supplier *</th>
                  <th className="px-2 py-2 text-left w-28">Type *</th>
                  <th className="px-2 py-2 text-left">Description *</th>
                  <th className="px-2 py-2 text-left w-40">Reference</th>
                  <th className="px-2 py-2 text-left w-60">Credit Account *</th>
                  <th className="px-2 py-2 text-right w-32">Amount *</th>
                  <th className="px-2 py-2 text-right w-24">Tax</th>
                  <th className="px-2 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <React.Fragment key={r.key}>
                    <tr className={`border-t border-gray-100 align-top ${rowErrors[r.key] ? 'bg-red-50' : ''}`} data-testid="bulk-row">
                      <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                      <td className="px-2 py-1.5">
                        <SearchableInventorySupplierDropdown value={r.supplier} displayLabel={r.supplierLabel}
                          onChange={(val, label) => update(r.key, { supplier: val || '', supplierLabel: label || '' })}
                          placeholder="Select supplier" menuPlacement="auto" />
                      </td>
                      <td className="px-2 py-1.5">
                        <select value={r.noteType} onChange={(e) => changeType(r, e.target.value)} className={input}>
                          {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.description} onChange={(e) => update(r.key, { description: e.target.value })} className={input}
                          placeholder={r.noteType === 'PENALTY' ? 'e.g. Late delivery — 3 days' : 'e.g. Volume rebate Q3'} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={r.reference} onChange={(e) => update(r.key, { reference: e.target.value })} className={input} placeholder="Invoice / PO / contract" />
                      </td>
                      <td className="px-2 py-1.5">
                        <SearchableLedgerDropdown value={r.ledgerAccountId} displayLabel={r.ledgerAccountLabel}
                          onChange={(id, lbl) => update(r.key, { ledgerAccountId: id || '', ledgerAccountLabel: lbl || '' })}
                          ledgerGroup={['REVENUE', 'EXPENSES']} placeholder="Select account…" menuPlacement="auto" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" min="0.01" step="0.01" value={r.amount} onChange={(e) => update(r.key, { amount: e.target.value })} className={`${input} text-right`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" min="0" step="0.01" value={r.noteType === 'PENALTY' ? '' : r.taxAmount} disabled={r.noteType === 'PENALTY'}
                          title={r.noteType === 'PENALTY' ? 'No tax on a penalty' : 'VAT reversed'}
                          onChange={(e) => update(r.key, { taxAmount: e.target.value })} className={`${input} text-right disabled:bg-gray-100`} />
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <button type="button" title="Copy row" onClick={() => duplicate(r)} className="text-gray-400 hover:text-amber-700 px-1">⧉</button>
                        {rows.length > 1 && (
                          <button type="button" title="Remove row" onClick={() => setRows(prev => prev.filter(x => x.key !== r.key))} className="text-gray-400 hover:text-red-600 px-1">✕</button>
                        )}
                      </td>
                    </tr>
                    {rowErrors[r.key] && (
                      <tr className="bg-red-50"><td></td><td colSpan={8} className="px-2 pb-1.5 text-xs text-red-600">{rowErrors[r.key]}</td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setRows(prev => [...prev, blankRow(prev[prev.length - 1]?.noteType || 'PENALTY')])}
              className="px-3 py-1 text-xs font-medium text-amber-700 border border-amber-300 rounded hover:bg-amber-50">+ Add row</button>
            <button type="button" onClick={() => setRows(prev => [...prev, ...Array.from({ length: 5 }, () => blankRow(prev[prev.length - 1]?.noteType || 'PENALTY'))])}
              className="px-3 py-1 text-xs font-medium text-amber-700 border border-amber-300 rounded hover:bg-amber-50">+ 5 rows</button>
            <span className="text-xs text-gray-500 self-center">Blank rows are ignored. ⧉ copies a row (type, description, account, amount) for another supplier.</span>
          </div>
        </div>

        <div className="flex-shrink-0 bg-gray-50 px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-700"><span className="font-semibold">{filled.length}</span> note(s) · total <span className="font-semibold text-amber-700">{fmt(total)}</span></span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving || !filled.length || !!closedError}
              className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
              {saving ? 'Creating…' : `Create ${filled.length || ''} Debit Note${filled.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebitNoteBulkEntry;
